import type {Dictionary} from '@malt/types-utils';
import type {Pinia, StateTree, Store, StoreDefinition, StoreGeneric} from 'pinia';

export interface AppContextOutput<TContext extends StateTree, TEntries extends string[]> {
    /** Fetch the API context, and patch the appContext Pinia stores with the returned state */
    loadApiContext: (pinia: Pinia, testing?: boolean) => Promise<void>;
    /** Create a typed API handler that will wrap all the boring http stuff and just expose a nice readable function
     *
     * @param endpoint - The endpoint key (ex: 'deleteCard')
     * @param overrideLinks - By default, the wrapper will use the links present in the context, but you can override by providing your own array
     *
     * @template TResult - The type of the result returned by the endpoint
     * @template TParams - The type of the requested body endpoint (optional)
     *
     * @example
     *
     * ```ts
        // Without params
    *  const getCards = createEndpoint<CardResource[]>('cards');
    *  await getCards();
    *
        // With params
    *  const directCardPayIn = createEndpoint<CardPaymentResponse, CardPaymentParams>('directCardPayIn');
    *  const response = await directCardPayIn({cardCvx: '333' ...etc});
    * ```
    */
    createEndpoint<TResult = void, TParams extends Dictionary = never>(
        endpoint: TEntries[number],
        options?: ApiRequestHandlerOptions,
    ): RequestHandler<TResult, TParams>;
    /** Pinia store stocking the api context */
    useAppContext: (pinia?: Pinia | null, hot?: StoreGeneric) => Store<'appContext', TContext & {loading: boolean}>;
    addLinks: (links: ResourceLink[]) => void;
    reloadContext: () => Promise<void>;
    /** Check if an endpoint exists in the registry */
    endpointExists: (key: TEntries[number], overrideLinks?: ResourceLink[]) => boolean;
    /** Check if an endpoint exists in the registry */
    getEndpointUrl: (key: TEntries[number], overrideLinks?: ResourceLink[]) => string | undefined;
}

export type RequestHandler<TResult, TParams extends Dictionary<string, any> = never> = [TParams] extends [never]
    ? () => Promise<TResult>
    : (params: TParams) => Promise<TResult>;

export interface ApiRequestHandlerOptions {
    overrideLinks?: ResourceLink[];
    method?: HttpMethodUnion;
    logger?: boolean;
}

export interface ResourceLink {
    context: ResourceLink[];
    href: string;
    rel: string;
    type?: HttpMethodUnion;
}

export interface ExtendedLink {
    url: string;
    method?: HttpMethodUnion;
}

export interface Resource {
    _links: ResourceLink[];
}

export enum HttpMethod {
    GET = 'GET',
    HEAD = 'HEAD',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH',
    DELETE = 'DELETE',
    OPTIONS = 'OPTIONS',
    TRACE = 'TRACE',
}

export type HttpMethodUnion = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS' | 'TRACE';
